import { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Send, Plus } from 'lucide-react';
import ChatAttachmentMenu from './ChatAttachmentMenu';
import AttachmentPreviewRow from './AttachmentPreviewRow';
import useDraft from '../../../../hooks/useDraft';

const MAX_ATTACHMENTS = 4;

function ChatSender({ onSend, chatId, otherUserId, onHeightChange }) {
  // State for message draft (persisted per chat)
  const [draft, setDraft, clearDraft] = useDraft(chatId);
  
  // State for attachments (images, calendar)
  const [attachments, setAttachments] = useState([]);
  // Controls visibility of attachment menu (+ button)
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  // Ref for textarea auto-resize
  const textareaRef = useRef(null);
  // Current user info from Redux
  const currentUserId = useSelector((s) => s.user.authUserId);
  const currentUserProfile = useSelector((s) => s.user.profile);

  // Send message with text and attachments, then clear state
  const submit = () => {
    const text = draft.trim();
    if (!text && attachments.length === 0) return;
    onSend(text, attachments);
    setAttachments([]);
    clearDraft();
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  // Handle file selection from attachment menu (images only for now)
  const handleFileSelect = (files, type, imageData = null) => {
    if (type === 'image') {
      const imageFiles = Array.from(files);
      const remainingSlots = MAX_ATTACHMENTS - attachments.length;
      const filesToAdd = imageFiles.slice(0, remainingSlots);
      
      const newAttachments = filesToAdd.map(file => ({
        id: `attachment-${Date.now()}-${Math.random()}`,
        type: 'image',
        file,
        preview: URL.createObjectURL(file),
        imageData: null,
      }));
      setAttachments(prev => [...prev, ...newAttachments]);
    } else if (type === 'camera') {
      // Camera capture - store file and metadata
      const file = Array.from(files)[0];
      const newAttachment = {
        id: `attachment-${Date.now()}-${Math.random()}`,
        type: 'image',
        file, // Store the file blob
        preview: imageData?.previewUrl || URL.createObjectURL(file),
        imageData, // Store metadata with source: 'camera'
      };
      setAttachments(prev => [...prev, newAttachment]);
    }
  };

  // Open camera for photo capture (not yet implemented)
  const handleCameraOpen = () => {
    // TODO: Implement camera opening logic
  };
 
  // Add calendar request attachment to message
  const handleAddRequestCalendar = () => {
    const calendarAttachment = {
      id: `calendar-${Date.now()}`,
      type: 'calendar_request',
      data: {
        requesterId: currentUserId,
      },
    };
    setAttachments(prev => [...prev, calendarAttachment]);
  };

  // Add calendar share attachment to message
  const handleAddShareCalendar = () => {
    const calendarAttachment = {
      id: `calendar-${Date.now()}`,
      type: 'calendar_share',
      data: {
        requesterId: currentUserId,
      },
    };
    setAttachments(prev => [...prev, calendarAttachment]);
  };

  // Remove attachment from preview by ID
  const handleRemoveAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  // Clear all attachments and draft text
  const handleClear = () => {
    setAttachments([]);
    setDraft('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  // Send message on Enter (shift+Enter for new line)
  const onTextareaKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // Auto-resize textarea as user types (max 160px height)
  const onTextareaInput = (e) => {
    setDraft(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  };

  const inputRef = useRef(null);

  // Track input area height and report to parent
  useEffect(() => {
    const el = inputRef.current;
    if (!el || !onHeightChange) return;
    const observer = new ResizeObserver(([entry]) => {
      onHeightChange(entry.contentRect.height + 1); // +1 for border-t
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [onHeightChange]);

  return (
    <>
      {/* Main input area */}
      <div ref={inputRef} className="absolute bottom-0 inset-x-0 border-t border-border bg-background p-2 z-10">
        {/* Attachment preview row */}
        <AttachmentPreviewRow
          attachments={attachments}
          onRemoveAttachment={handleRemoveAttachment}
        />
        {/* Text input and buttons */}
        <div className="flex items-end gap-2">
          <div className="relative flex-1 rounded-2xl border border-input bg-background">
            <textarea
              ref={textareaRef}
              value={draft}
              onChange={onTextareaInput}
              onKeyDown={onTextareaKeyDown}
              placeholder="Message..."
              rows={1}
              className="block w-full resize-none bg-transparent px-4 py-2 text-sm focus:outline-none"
              style={{ maxHeight: '160px' }}
            />
            {/* Attachment menu trigger button */}
            <button
              onClick={() => setShowAttachmentMenu(true)}
              className="absolute bottom-[5px] right-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-blue-200 text-blue-700 opacity-80 hover:bg-blue-300"
              aria-label="Attach images"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          {/* Send button */}
          <button
            onClick={submit}
            disabled={!draft.trim() && attachments.length === 0}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
      {/* Attachment menu overlay */}
      {showAttachmentMenu && (
        <ChatAttachmentMenu
          onClose={() => setShowAttachmentMenu(false)}
          onFileSelect={handleFileSelect}
          onAddRequestCalendar={handleAddRequestCalendar}
          onAddShareCalendar={handleAddShareCalendar}
          onClear={handleClear}
          chatId={chatId}
        />
      )}
    </>
  );
}

export default ChatSender;
